import type {
  MemberDetails,
  PatientScreenerAnswers,
  SharedData,
  UserRole,
} from "@src/context/ApplicationContext";
import { calculateAgeInYears, getAgeRange, ISO_DATE_ONLY_PATTERN } from "@src/utils/date-utils";
import { normalizeEmail, normalizeString } from "@src/utils/string-utils";
import type {
  SalesforceCanvasInsurancePayload,
  SalesforceCanvasPatientPayload,
  SalesforceCanvasPrefillPayload,
  SalesforceCanvasScreenerPayload,
} from "./types";

const PATIENT_GENDER_MAP = {
  M: "m",
  F: "f",
} as const;

const PRONOUNS_MAP = {
  "He/him/his": "He/Him",
  "She/her/hers": "She/Her",
  "They/them/theirs": "They/Them",
} as const;

const POLICYHOLDER_GENDER_MAP = {
  M: "m",
  F: "f",
} as const;

/**
 * Salesforce asks "Primary Subscriber Relation" (policyholder's relationship
 * to the patient), while our app asks "Your relationship to the primary
 * holder" (patient's relationship to the policyholder).  "Parent" in
 * Salesforce means the policyholder is the patient's parent, so the patient
 * is the policyholder's child.
 */
const RELATIONSHIP_MAP = {
  Parent: "child",
  Spouse: "spouse",
  Other: "other",
} as const;

function stripUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      return entry !== undefined;
    }),
  ) as Partial<T>;
}

function formatDateInputValue(year: number, month: number, day: number): string | undefined {
  const date = new Date(year, month - 1, day);
  const isValidDate =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  if (!isValidDate) {
    return undefined;
  }

  const paddedMonth = String(month).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");
  return `${paddedMonth}/${paddedDay}/${year}`;
}

function normalizeCanvasDate(value?: string): string | undefined {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const salesforceDateMatch = ISO_DATE_ONLY_PATTERN.exec(trimmedValue);
  if (salesforceDateMatch) {
    return formatDateInputValue(
      Number(salesforceDateMatch[1]),
      Number(salesforceDateMatch[2]),
      Number(salesforceDateMatch[3]),
    );
  }

  return undefined;
}

/**
 * Formats a raw digit string (e.g. "6191112222") into the NANP display
 * format the app uses everywhere: `(619) 111-2222`.
 * Returns `undefined` for non-10-digit inputs so the field is skipped.
 */
function formatPhoneToNANP(value?: string): string | undefined {
  const digits = value?.replace(/\D/g, "");
  if (digits?.length !== 10) {
    return undefined;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function boolToYesNo(value: boolean | undefined): "Yes" | "No" | undefined {
  if (value === undefined) return undefined;
  return value ? "Yes" : "No";
}

function boolToSubscribedStatus(
  value: boolean | undefined,
): "subscribed" | "unsubscribed" | undefined {
  if (value === undefined) return undefined;
  return value ? "subscribed" : "unsubscribed";
}

function mapPatientPatch(
  patient?: SalesforceCanvasPatientPayload,
  isNavigator?: boolean,
): Partial<SharedData> {
  if (!patient) {
    return {};
  }

  const dateOfBirth = normalizeCanvasDate(patient.dateOfBirth);
  const legalSex = patient.genderAtBirth ? PATIENT_GENDER_MAP[patient.genderAtBirth] : undefined;
  const email = normalizeEmail(patient.email);
  const ageRange = dateOfBirth
    ? (getAgeRange(calculateAgeInYears(dateOfBirth)) ?? undefined)
    : undefined;

  const memberDetails = stripUndefined<MemberDetails>({
    firstName: patient.firstName,
    lastName: patient.lastName,
    phone: formatPhoneToNANP(patient.phone),
    phoneType: "mobile",
    preferredContactMethod: "Phone/SMS",
    voicemailPreference: "no",
    voicemailConsent: isNavigator ? boolToYesNo(patient.primaryVoicemailConsent) : undefined,
    smsConsent: isNavigator ? boolToSubscribedStatus(patient.primarySMSOptIn) : undefined,
    email,
    streetAddress1: patient.streetAddress1,
    streetAddress2: patient.streetAddress2,
    city: patient.city,
    state_code: patient.state_code,
    zip: patient.zip,
    language: patient.language,
    preferredPronouns: patient.pronouns ? PRONOUNS_MAP[patient.pronouns] : undefined,
    legalSex,
  });

  return stripUndefined<Partial<SharedData>>({
    salesforceLeadId: normalizeString(patient.leadId),
    memberDetails: Object.keys(memberDetails).length > 0 ? memberDetails : undefined,
    zip: patient.zip,
    dob: dateOfBirth,
    ageRange,
    gender: legalSex,
    testRecord: patient.testRecord === true ? true : undefined,
  });
}

function mapSupportedRelationship(
  relationship?: SalesforceCanvasInsurancePayload["relationshipToPolicyholder"],
): "Self" | "spouse" | "child" | "other" | undefined {
  switch (relationship) {
    case "Self":
      return relationship;
    case "Spouse":
    case "Parent":
    case "Other":
      return RELATIONSHIP_MAP[relationship];
    default:
      return undefined;
  }
}

function mapCoverageInsurancePatch(
  insurance: SalesforceCanvasInsurancePayload,
): Partial<SharedData> {
  const relationshipToPolicyholder = mapSupportedRelationship(insurance.relationshipToPolicyholder);
  const policyholderDOB = normalizeCanvasDate(insurance.policyholderDOB);

  let isPrimaryPolicyHolder: boolean | undefined;
  if (relationshipToPolicyholder === "Self") {
    isPrimaryPolicyHolder = true;
  } else if (insurance.isPrimaryPolicyHolder !== undefined) {
    isPrimaryPolicyHolder = insurance.isPrimaryPolicyHolder === "Yes";
  }

  return stripUndefined<Partial<SharedData>>({
    paymentMethod: "insurance",
    memberId: insurance.memberId,
    policyholderFirstName: insurance.policyholderFirstName,
    policyholderLastName: insurance.policyholderLastName,
    policyholderDOB,
    policyholderGender: insurance.policyholderGenderAtBirth
      ? POLICYHOLDER_GENDER_MAP[insurance.policyholderGenderAtBirth]
      : undefined,
    relationshipToPolicyholder:
      relationshipToPolicyholder === "Self" ? "" : relationshipToPolicyholder,
    isPrimaryPolicyHolder,
    insurancePolicy: insurance.packageId
      ? {
          id: String(insurance.packageId),
          name: insurance.insurancePlanType ?? "",
        }
      : undefined,
    selectedPolicy: insurance.packageId ? String(insurance.packageId) : undefined,
  });
}

function mapInsurancePatch(insurance?: SalesforceCanvasInsurancePayload): Partial<SharedData> {
  if (!insurance || insurance.selfPay === undefined) {
    return {};
  }

  if (insurance.selfPay) {
    return {
      paymentMethod: "self_pay",
    };
  }

  return mapCoverageInsurancePatch(insurance);
}

function yesNoToBoolean(value: "Yes" | "No" | null | undefined): boolean | undefined {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return undefined;
}

function mapScreenerPatch(screener?: SalesforceCanvasScreenerPayload): Partial<SharedData> {
  if (!screener) return {};

  const answers = stripUndefined<PatientScreenerAnswers>({
    hospitalized: yesNoToBoolean(screener.hospitalizedPast30Days),
    eatingDisorder: yesNoToBoolean(screener.activeEatingDisorder),
    seriousMentalIllness: yesNoToBoolean(screener.seriousMentalIllness),
    longActingInjectable: yesNoToBoolean(screener.longActingInjectables),
    opioidTreatment: yesNoToBoolean(screener.opioidTreatmentMedications),
    controlledSubstances: yesNoToBoolean(screener.controlledSubstances),
  });

  if (Object.keys(answers).length === 0) return {};

  return { patientScreenerAnswers: answers };
}

export function mapCanvasEventPayloadToSharedDataPatch(
  payload: unknown,
  userRole?: UserRole,
): Partial<SharedData> {
  if (!payload || typeof payload !== "object") {
    return {};
  }

  const { patient, insurance, screener } = payload as SalesforceCanvasPrefillPayload;

  return {
    ...mapPatientPatch(patient, userRole === "patient navigator"),
    ...mapInsurancePatch(insurance),
    ...mapScreenerPatch(screener),
  };
}
